import { z } from 'zod';
import {
    createZodFormResolver,
    getDefaults,
    useDateTimeLocalizationInfo,
    lt,
    DateTimeLocalizationInfo,
    MAX_INPUT_TEXT_LENGTH,
    useFormControls,
    LocalizedText,
    SubmitHandler,
    Validation,
} from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Customer, Sex, TitlePrefix } from '@mews-commander/data-access';
import { DateField, SchemaFormDefinition, SchemaFormFields } from '@mews-commander/form';
import { Button, ButtonProps, Stack } from '@optimus-web/core';
import { Option, WithEnterpriseId } from '@mews-commander/types';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { getCountryOptions } from '@mews-commander/legacy-entities/src/ducks/countries';
import { getLanguageOptions } from '@mews-commander/legacy-entities/src/ducks/languages';
import { OverlayedLoader } from '@mews-commander/ui';
import { createCustomerProfile } from '../../actions';

const createProfileSchema = ({
    dateTimeLocalizationInfo,
    lastName,
}: {
    dateTimeLocalizationInfo: DateTimeLocalizationInfo;
    lastName: string;
}) =>
    z.object({
        firstName: z.string().trim().max(MAX_INPUT_TEXT_LENGTH).default(''),
        lastName: Validation.String.Required.default(lastName),
        email: z.string().email().or(z.literal('')).default(''),
        preferredLanguageCode: Validation.String.Empty.default(''),
        nationalityCode: Validation.String.Empty.default(''),
        gender: z.nativeEnum(Sex).nullish().or(z.literal('')).default(''),
        birthDateString: Validation.BirthDate.WallClock(dateTimeLocalizationInfo).nullish(),
        titlePrefix: z.nativeEnum(TitlePrefix).nullish().or(z.literal('')).default(''),
        telephone: z.string().trim().max(MAX_INPUT_TEXT_LENGTH).default(''),
    });

type ZodSchemaValues = z.output<ReturnType<typeof createProfileSchema>>;

const createFormDefinition = ({
    countryOptions,
    languageOptions,
}: {
    countryOptions: Option[];
    languageOptions: Option[];
}): SchemaFormDefinition<ReturnType<typeof createProfileSchema>> => ({
    fields: {
        email: {
            label: lt('Email'),
        },
        titlePrefix: {
            label: lt('Title'),
            field: { type: 'enum', enumPrefix: 'TitlePrefix', showEmptyValue: true },
        },
        firstName: {
            label: lt('FirstName'),
        },
        lastName: {
            label: lt('LastName'),
        },
        preferredLanguageCode: {
            label: lt('PreferredLanguage'),
            field: {
                type: 'select',
                options: languageOptions,
                showEmptyValue: true,
            },
        },
        nationalityCode: {
            label: lt('Nationality'),
            field: {
                type: 'select',
                options: countryOptions,
                showEmptyValue: true,
            },
        },
        gender: {
            label: lt('Sex'),
            field: { type: 'enum', enumPrefix: 'Sex', showEmptyValue: true },
        },
        telephone: {
            label: lt('Phone'),
        },
        birthDateString: {
            label: lt('BirthDate'),
            render: (props) => <DateField mode="localized" {...props} />,
        },
    },
});

interface ProfileCreateFormProps extends WithEnterpriseId {
    lastName: string;
    onSuccess: (customer: Customer) => void;
    form: string;
    submitButtonText?: Mews.TextKey;
    /**
     * Whether to add the loader or not, you can use `isPending` prop to show the loader on your async onSuccess action
     */
    withLoader?: boolean;
    isPending?: boolean;
    /**
     * If true, the form will be reset after a successful submission
     */
    resetFormOnSuccess?: boolean;
    buttonVariant?: ButtonProps['variant'];
}

export const ProfileCreateForm = ({
    form,
    lastName,
    onSuccess,
    enterpriseId,
    submitButtonText = TextKey.Create,
    isPending,
    resetFormOnSuccess,
    withLoader,
    buttonVariant = 'primary',
}: ProfileCreateFormProps) => {
    const dispatch = useCommanderThunkDispatch();

    const onSubmit: SubmitHandler<ZodSchemaValues> = async (formData, { reset }) => {
        const finalFormData = {
            ...formData,
            gender: formData.gender || null,
            titlePrefix: formData.titlePrefix || null,
            preferredLanguageCode: formData.preferredLanguageCode || null,
            nationalityCode: formData.nationalityCode || null,
        };

        await dispatch(createCustomerProfile(finalFormData, enterpriseId)).then((customer: $TSFixMe) => {
            onSuccess(customer);
            if (resetFormOnSuccess) {
                reset();
            }
        });
    };

    const countryOptions = useCommanderSelector(getCountryOptions);
    const languageOptions = useCommanderSelector(getLanguageOptions);
    const formDefinition = createFormDefinition({ countryOptions, languageOptions });

    const dateTimeLocalizationInfo = useDateTimeLocalizationInfo();
    const profileSchema = createProfileSchema({ dateTimeLocalizationInfo, lastName });

    const { renderFormContent, isSubmitting, isValid } = useFormControls<ZodSchemaValues>({
        onSubmit,
        mode: 'onTouched',
        id: form,
        defaultValues: getDefaults(profileSchema),
        resolver: createZodFormResolver(profileSchema),
    });

    return renderFormContent(
        <Stack vertical spacing="200">
            {withLoader && <OverlayedLoader show={isPending || isSubmitting} />}
            <SchemaFormFields schema={profileSchema} formDefinition={formDefinition} />
            <Stack alignment="end">
                <Button disabled={!isValid} type="submit" variant={buttonVariant}>
                    <LocalizedText textKey={submitButtonText} />
                </Button>
            </Stack>
        </Stack>
    );
};
